import { useMemo } from 'react';
import { NegotiationHistory } from '~/features/shared/market-content/NegotiationHistory';
import {
  formatNegotiationHistoryTerm,
  headlineValue,
  MSAAS_TERM_GROUPS,
  msaasChainToVersions,
  negotiationHistoryTermLabel,
} from '../../negotiations/negotiationHistory';
import { useNegotiationChain } from '../../negotiations/useNegotiationChain';
import { API_CALLS } from '../api';
import type { B2BLatestNegotiation, B2BNegotiationSummary } from '../types';
import { formatHourlyRate } from '../utils';

type Props = {
  responseId: number;
  latestNegotiation: B2BLatestNegotiation | null;
  negotiationSummaries: B2BNegotiationSummary[];
  /** Who the viewer is on the chain: the responder (Sent) or the listing owner (Received). */
  viewerSender: 'RESPONDER' | 'LISTING_OWNER';
};

/** The negotiation card for a B2B response; the rounds are read when the history opens. */
export const B2BNegotiationHistory = ({
  responseId,
  latestNegotiation,
  negotiationSummaries,
  viewerSender,
}: Props) => {
  const history = useNegotiationChain(responseId, API_CALLS.getNegotiationChain);
  const versions = useMemo(
    () => (history.rows ? msaasChainToVersions(history.rows) : null),
    [history.rows]
  );
  const latestSummary = negotiationSummaries.find(
    (summary) => summary.version === latestNegotiation?.version
  );

  return (
    <NegotiationHistory
      rounds={negotiationSummaries.length}
      latest={
        latestNegotiation && {
          version: latestNegotiation.version,
          party: latestSummary?.party ?? null,
          headline: {
            ftes: latestNegotiation.numOfFtes != null ? String(latestNegotiation.numOfFtes) : null,
            rate: formatHourlyRate(latestNegotiation.pricePerHour) || null,
            value: headlineValue(latestNegotiation.totalContractValue),
          },
        }
      }
      viewerSender={viewerSender}
      versions={versions}
      loading={history.loading}
      error={history.error}
      expanded={history.expanded}
      onToggle={history.toggle}
      termGroups={MSAAS_TERM_GROUPS}
      termLabel={negotiationHistoryTermLabel}
      formatTerm={formatNegotiationHistoryTerm}
      cy={{ toggle: 'btn-toggle-full-history', history: 'full-history' }}
    />
  );
};
